import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import CheckBox from 'commons/src/components/input/Checkbox';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import styles from './SelectionModal.module.scss';

export type SelectionModalItem = {
    id: string;
    label: string;
    tag?: string;
    searchableText?: string;
};

export type ColumnActions = {
    showSelectAll?: boolean;
    showClearAll?: boolean;
    showFilter?: boolean;
    onFilter?: () => void;
};

export type SelectionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (selectedIds: string[]) => void;
    items: SelectionModalItem[];
    selectedIds: string[];

    searchPosition?: 'left-column' | 'top';
    showColumnActions?: boolean;
    compactButtons?: boolean;

    title: string;
    availableTitle: string;
    selectedTitle: string;
    searchPlaceholder?: string;
    saveButtonLabel?: string;

    loading?: boolean;
    loadingMessage?: string;
    emptyMessage: string;
    allSelectedMessage: string;
    noSearchResultsMessage?: string;
    noSelectionMessage: string;

    availableColumnActions?: ColumnActions;
    selectedColumnActions?: ColumnActions;

    className?: string;
};

const SelectionModal: React.FC<SelectionModalProps> = ({
    isOpen,
    onClose,
    onSave,
    items,
    selectedIds,
    searchPosition = 'left-column',
    showColumnActions = false,
    compactButtons = false,
    title,
    availableTitle,
    selectedTitle,
    searchPlaceholder,
    saveButtonLabel,
    loading = false,
    loadingMessage,
    emptyMessage,
    allSelectedMessage,
    noSearchResultsMessage,
    noSelectionMessage,
    availableColumnActions,
    selectedColumnActions,
    className,
}) => {
    const { t: txt } = useTranslation();

    const [searchText, setSearchText] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedIds);
    const [leftChecked, setLeftChecked] = useState<string[]>([]);
    const [rightChecked, setRightChecked] = useState<string[]>([]);

    useEffect(() => {
        if (isOpen) {
            setTempSelectedIds(selectedIds);
            setLeftChecked([]);
            setRightChecked([]);
            setSearchText('');
        }
    }, [isOpen, selectedIds]);

    const filteredItems = useMemo(() => {
        if (!searchText) return items;
        const searchLower = searchText.toLowerCase();
        return items.filter(item => {
            const labelMatch = item.label.toLowerCase().includes(searchLower);
            const extraMatch = item.searchableText?.toLowerCase().includes(searchLower) ?? false;
            return labelMatch || extraMatch;
        });
    }, [items, searchText]);

    const tempSelectedIdsSet = useMemo(() => new Set(tempSelectedIds), [tempSelectedIds]);

    const availableItems = useMemo(() => {
        return filteredItems.filter(item => !tempSelectedIdsSet.has(item.id));
    }, [filteredItems, tempSelectedIdsSet]);

    const selectedItems = useMemo(() => {
        return filteredItems.filter(item => tempSelectedIdsSet.has(item.id));
    }, [filteredItems, tempSelectedIdsSet]);

    const handleLeftCheckboxChange = (itemId: string, checked: boolean): void => {
        if (checked) {
            setLeftChecked(prev => (prev.includes(itemId) ? prev : [...prev, itemId]));
        } else {
            setLeftChecked(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleRightCheckboxChange = (itemId: string, checked: boolean): void => {
        if (checked) {
            setRightChecked(prev => (prev.includes(itemId) ? prev : [...prev, itemId]));
        } else {
            setRightChecked(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleAdd = (): void => {
        setTempSelectedIds(prev => {
            const existing = new Set(prev);
            const newIds = leftChecked.filter(id => !existing.has(id));
            return [...prev, ...newIds];
        });
        setLeftChecked([]);
    };

    const handleRemove = (): void => {
        setTempSelectedIds(prev => prev.filter(id => !rightChecked.includes(id)));
        setRightChecked([]);
    };

    const handleSave = (): void => {
        onSave(tempSelectedIds);
        setLeftChecked([]);
        setRightChecked([]);
        setSearchText('');
        onClose();
    };

    const handleClose = (): void => {
        setTempSelectedIds(selectedIds);
        setLeftChecked([]);
        setRightChecked([]);
        setSearchText('');
        onClose();
    };

    const handleSelectAllAvailable = (): void => {
        setLeftChecked(availableItems.map(item => item.id));
    };

    const handleClearAllAvailable = (): void => {
        setLeftChecked([]);
    };

    const handleSelectAllSelected = (): void => {
        setRightChecked(selectedItems.map(item => item.id));
    };

    const handleClearAllSelected = (): void => {
        setRightChecked([]);
    };

    const resolvedSearchPlaceholder = searchPlaceholder ?? txt('Search');
    const resolvedSaveButtonLabel = saveButtonLabel ?? txt('Save');
    const resolvedLoadingMessage = loadingMessage ?? txt('Loading');

    const leftActions = availableColumnActions ?? {};
    const rightActions = selectedColumnActions ?? {};

    const renderSearchInput = (): React.ReactNode => (
        <div className={styles.searchInputWrapper}>
            <MaterialIcon name="search" extraClass={styles.searchIcon} />
            <input
                type="text"
                className={styles.searchInput}
                placeholder={resolvedSearchPlaceholder}
                value={searchText}
                onChange={(e): void => setSearchText(e.target.value)}
            />
        </div>
    );

    const renderActionBar = (
        actions: ColumnActions,
        onSelectAll: () => void,
        onClearAll: () => void
    ): React.ReactNode => {
        const { showSelectAll = true, showClearAll = true, showFilter = false, onFilter } = actions;
        return (
            <div className={styles.actionBar}>
                {showSelectAll && (
                    <button type="button" className={styles.actionLink} onClick={onSelectAll}>
                        {txt('SelectAll')}
                    </button>
                )}
                {showClearAll && (
                    <button type="button" className={styles.actionLink} onClick={onClearAll}>
                        {txt('ClearAll')}
                    </button>
                )}
                {showFilter && onFilter && (
                    <button type="button" className={styles.actionLink} onClick={onFilter}>
                        <MaterialIcon name="filter_list" extraClass={styles.filterIcon} />
                        {txt('Commons.Filter')}
                    </button>
                )}
            </div>
        );
    };

    const renderItemRow = (
        item: SelectionModalItem,
        side: 'left' | 'right',
        checked: boolean,
        onChange: (id: string, val: boolean) => void
    ): React.ReactNode => (
        <div key={item.id} className={styles.itemRow}>
            <CheckBox
                id={`${side}-${item.id}`}
                checked={checked}
                onChange={(e): void => onChange(item.id, e.currentTarget.checked)}
                label={
                    <div className={styles.itemLabel}>
                        <span>{item.label}</span>
                        {item.tag && <span className={styles.tag}>{item.tag}</span>}
                    </div>
                }
                labelIsElement
            />
        </div>
    );

    const renderAvailableContent = (): React.ReactNode => {
        if (loading) {
            return <div className={styles.emptyMessage}>{resolvedLoadingMessage}</div>;
        }
        if (availableItems.length === 0 && items.length === 0) {
            return <div className={styles.emptyMessage}>{emptyMessage}</div>;
        }
        if (availableItems.length === 0 && searchText) {
            return <div className={styles.emptyMessage}>{noSearchResultsMessage ?? txt('NoResults')}</div>;
        }
        if (availableItems.length === 0) {
            return <div className={styles.emptyMessage}>{allSelectedMessage}</div>;
        }
        return availableItems.map(item =>
            renderItemRow(item, 'left', leftChecked.includes(item.id), handleLeftCheckboxChange)
        );
    };

    const buttonContainerClass = compactButtons ? styles.columnButtonContainerCompact : styles.columnButtonContainer;

    const customHeader = (
        <div className={styles.header}>
            <h1 className={styles.headerTitle}>{title}</h1>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label={txt('Close')}>
                <MaterialIcon name="close" />
            </button>
        </div>
    );

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={handleClose}
            size="large"
            customHeader={customHeader}
            className={`${styles.selectionModal}${className ? ` ${className}` : ''}`}
        >
            <div className={styles.modalContent}>
                {searchPosition === 'top' && <div className={styles.searchContainerTop}>{renderSearchInput()}</div>}
                <div className={styles.columnsContainer}>
                    <div className={styles.column}>
                        <h3 className={styles.columnHeader}>{availableTitle}</h3>
                        {showColumnActions &&
                            renderActionBar(leftActions, handleSelectAllAvailable, handleClearAllAvailable)}
                        <div className={styles.availableBox}>
                            {searchPosition === 'left-column' && (
                                <div className={styles.searchContainerInColumn}>{renderSearchInput()}</div>
                            )}
                            <div className={styles.itemList}>{renderAvailableContent()}</div>
                        </div>
                        <div className={buttonContainerClass}>
                            <PrimaryButton
                                title={txt('Add')}
                                onClick={handleAdd}
                                disabled={leftChecked.length === 0}
                                color="primary"
                                fullWidth={!compactButtons}
                                trailingIcon={<MaterialIcon name="arrow_forward" />}
                            />
                        </div>
                    </div>
                    <div className={styles.column}>
                        <h3 className={styles.columnHeader}>
                            {selectedTitle} ({tempSelectedIds.length})
                        </h3>
                        {showColumnActions &&
                            renderActionBar(rightActions, handleSelectAllSelected, handleClearAllSelected)}
                        <div className={styles.selectedBox}>
                            <div className={styles.itemList}>
                                {selectedItems.length === 0 ? (
                                    <div className={styles.emptyMessage}>{noSelectionMessage}</div>
                                ) : (
                                    selectedItems.map(item =>
                                        renderItemRow(
                                            item,
                                            'right',
                                            rightChecked.includes(item.id),
                                            handleRightCheckboxChange
                                        )
                                    )
                                )}
                            </div>
                        </div>
                        <div className={buttonContainerClass}>
                            <PrimaryButton
                                title={txt('Remove')}
                                onClick={handleRemove}
                                disabled={rightChecked.length === 0}
                                color="secondary"
                                fullWidth={!compactButtons}
                                icon={<MaterialIcon name="arrow_back" />}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.saveButtonContainer}>
                    <PrimaryButton title={resolvedSaveButtonLabel} onClick={handleSave} color="primary" />
                </div>
            </div>
        </ModalWrapper>
    );
};

export default SelectionModal;
